import { Button, Table, Input, Space, Tooltip, message } from "antd";
import { useState, useRef } from "react";
import moment from "moment";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import "../styles/App.css";
import { downloadFile } from "../utils/downloadFile.js";

const FileAPIData = (props) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [tableKey, setTableKey] = useState(0);

  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const { bucketName, list } = props;

  const text = <span> Search</span>;

  const start = () => {
    setLoading(true);
    setTimeout(() => {
      setSelectedRowKeys([]);
      setSearchText("");
      setSearchedColumn(0);
      setTableKey((tableKey) => tableKey + 1);
      setLoading(false);
    }, 1000);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchedColumn("FileName");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Tooltip placement="topLeft" title={text}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys, confirm, dataIndex);
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
          </Tooltip>
          <Button
            onClick={() => {
              handleReset(clearFilters);
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
              handleSearch("", confirm, dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Clear
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#0079FF" : undefined,
          fontSize: "xx-large",
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Bulk download
  const handleDownloadSelection = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select files to download");
      return;
    }

    // If too many files selected
    if (selectedRowKeys.length > 10) {
      message.warning(
        "Please select 10 or fewer files for reliable downloading"
      );
    }

    setDownloadInProgress(true);
    //setDownloadProgress(0);

    try {
      // Start downloads in sequence without visible progress updates
      for (let i = 0; i < selectedRowKeys.length; i++) {
        const fileName = selectedRowKeys[i];

        // Start download without displaying loading messages
        await downloadFile(props.bucketName, fileName, fileName);

        // Small delay between downloads to prevent browser throttling
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      // Reset state after all downloads initiated
      setDownloadInProgress(false);
    }
  };

  const columns = [
    {
      title: "File Name",
      dataIndex: "FileName",
      key: "FileName",
      sorter: (a, b) => a.FileName > b.FileName,
      ...getColumnSearchProps("FileName"),
    },
    {
      title: "Size (MB)",
      dataIndex: "Size",
      key: "Size",
      sorter: (a, b) => a.Size > b.Size,
      width: 80,
    },
    {
      title: "Creation Time",
      dataIndex: "CreationTime",
      key: "CreationTime",
      render: (CreationTime) => (
        <p>{moment(CreationTime).format("MM/DD/YYYY, h:mm:ss a")}</p>
      ),
      sorter: (a, b) => new Date(a.CreationTime) - new Date(b.CreationTime),
    },
    {
      title: "Action",
      dataIndex: "FileName",
      key: "Action",
      render: (text, record) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            downloadFile(bucketName, record.FileName, record.FileName);
          }}
        >
          Download
        </a>
      ),
    },
  ];

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <div>
      <div className="dtext">
        <br />
        <p style={{ color: "red" }}>
          NOTE - Please be aware that due to size of these files, attempting to
          download multiple files simultaneously through the browser may result
          in unsuccessful downloads. We strongly recommend limiting your
          simultaneous downloads to no more than 10 files at a time. This
          approach increases the likelihood of successful download of the files.
        </p>
      </div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexDirection: "row",
          gap: "2vh",
        }}
      >
        <Button
          className="buttoncolor"
          type="primary"
          onClick={start}
          disabled={!hasSelected || downloadInProgress}
          loading={loading}
        >
          Clear
        </Button>
        <div>
          <Button
            className="buttoncolor"
            type="primary"
            onClick={handleDownloadSelection}
            //disabled={ downloadInProgress}
            // loading={downloadInProgress}
            // disabled={!hasSelected}
          >
            Download
          </Button>
          &nbsp;
        </div>
        <span style={{ marginLeft: 8 }}>
          {hasSelected ? `Selected ${selectedRowKeys.length} items` : ""}
        </span>
      </div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        rowKey={(record) => record.FileName}
        dataSource={props.list}
        key={tableKey}
      />
    </div>
  );
};

export default FileAPIData;
